import { useIsMounted } from '@ui/hooks/useIsMounted';
import { getLocalStorageKey } from '@ui/utils/getLocalStorageKey';
import { safeParseJson } from '@ui/utils/safeParseJson';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type SupportPersistValueType = number | string | boolean;

export type ValueState<T extends SupportPersistValueType> = {
  _persistValue: T;
  setPersistValue: (value: T) => void;
  useDebouncedPersistState: (
    debounceTimeout: number,
  ) => [T, Dispatch<SetStateAction<T>>];
};

const getInitValue = <T extends SupportPersistValueType>(
  keyName: string,
  defaultValue: T,
) => {
  if (global?.window?.localStorage) {
    const localJson = localStorage.getItem(getLocalStorageKey(keyName));
    const parsed = safeParseJson<{
      state: { _persistValue: ValueState<T>['_persistValue'] };
    }>(localJson || '')?.state?._persistValue;
    const initValue = typeof parsed === 'number' ? parsed : defaultValue;
    
return initValue;
  }

  return defaultValue;
};

export const createPersistValue = <T extends number | string | boolean>(
  keyName: string,
  defaultValue: T,
  version?: number,
) => {
  const usePersistValue = create<ValueState<T>>()(
    persist(
      (set) => ({
        _persistValue: getInitValue(keyName, defaultValue),
        usePersistValue: () => {
          throw new Error(
            'usePersistValue can only be used inside React Component',
          );
        },
        useDebouncedPersistState: (debounceTimeout: number) => {
          throw new Error(
            'useDebouncedPersistState can only be used inside React Component',
          );
        },
        setPersistValue: (persistValue: T) => {
          throw new Error(
            'setPersistValue can only be used inside React Component',
          );
        },
      }),
      {
        name: getLocalStorageKey(keyName),
        storage: createJSONStorage(() => localStorage),
        version: version ?? 1,
      },
    ),
  );

  const useValue = create<ValueState<T>>()((set, get) => ({
    _persistValue: getInitValue(keyName, defaultValue),
    useDebouncedPersistState: (debounceTimeout: number) => {
      const v = get();
      const hooks = useState(v._persistValue);
      useEffect(() => {
        const handler = setTimeout(() => {
          if (typeof requestIdleCallback === 'function') {
            requestIdleCallback(() => {
              usePersistValue.setState({ _persistValue: hooks[0] });
            });
          } else {
            usePersistValue.setState({ _persistValue: hooks[0] });
          }
        }, debounceTimeout);
        const h2 = setTimeout(() => {
          set({ _persistValue: hooks[0] });
        });
        
return () => {
          clearTimeout(handler);
          clearTimeout(h2);
        };
      }, [hooks[0]]);
      
return hooks;
    },
    setPersistValue: (persistValue: T) => {
      usePersistValue.setState({ _persistValue: persistValue });
      set({ _persistValue: persistValue });
    },
  }));
  
return useValue;
};
